import type { MatrixPos, Priority } from "@app/core";
import { clamp01, matrixCellCenter, matrixScore } from "@app/core";
import type { I18nKey } from "../lib/language.js";

/**
 * Shared task color palette. The dialog and the context-menu both render
 * a swatch grid; keeping the list in one place stops them from drifting apart.
 * `value` is the hex string we persist on `Task.custom_color`; `null` clears
 * the color override. `label` is the accessible label / tooltip.
 */
export interface TaskColor {
  label: string;
  value: string | null;
}

export const TASK_COLOR_PALETTE: ReadonlyArray<TaskColor> = [
  { label: "No color", value: null },
  { label: "Red",    value: "#ef4444" },
  { label: "Orange", value: "#f97316" },
  { label: "Yellow", value: "#eab308" },
  { label: "Green",  value: "#22c55e" },
  { label: "Teal",   value: "#14b8a6" },
  { label: "Blue",   value: "#3b82f6" },
  { label: "Purple", value: "#a855f7" },
  { label: "Pink",   value: "#ec4899" },
  { label: "Lime",   value: "#84cc16" },
  { label: "Cyan",   value: "#06b6d4" },
  { label: "Indigo", value: "#6366f1" },
  { label: "Slate",  value: "#64748b" },
];

/** `TaskColor.label` → i18n key. The palette's labels are stable English ids
 *  (they key the persisted swatch), so every swatch UI translates through this
 *  map rather than rendering `label` directly. */
export const COLOR_LABEL_KEYS: Record<string, I18nKey> = {
  "No color": "color.noColor",
  Red: "color.red",
  Orange: "color.orange",
  Yellow: "color.yellow",
  Green: "color.green",
  Teal: "color.teal",
  Blue: "color.blue",
  Purple: "color.purple",
  Pink: "color.pink",
  Lime: "color.lime",
  Cyan: "color.cyan",
  Indigo: "color.indigo",
  Slate: "color.slate",
};

/** Accent hex used for the priority stripe on calendar chips/blocks. "" = none. */
export const PRIORITY_HEX: Record<Priority, string> = {
  high: "#ef4444",
  medium: "#f59e0b",
  low: "#60a5fa",
  none: "",
};

/** Traffic-light palette for the priority flag in the task form. Intentionally
 *  distinct from {@link PRIORITY_HEX} (which tints calendar chips). */
export const PRIORITY_FLAG_HEX: Record<Priority, string> = {
  none: "",
  low: "#22c55e",
  medium: "#eab308",
  high: "#ef4444",
};

/** Neutral base color for an event with no priority accent and no custom color. */
export const EVENT_DEFAULT_HEX = "#64748b"; // slate-500

/** The matrix diagonal score of a position: 0 at bottom-left (low urgency, low
 *  importance) → 1 at top-right (high/high). Alias of core's `matrixScore` —
 *  the one copy of the diagonal math. */
export const matrixScoreOf: (pos: MatrixPos) => number = matrixScore;

const MATRIX_STOP_HEX = [PRIORITY_FLAG_HEX.low, PRIORITY_FLAG_HEX.medium, PRIORITY_FLAG_HEX.high];

/** Color on the matrix's diagonal green→red gradient (green at bottom-left,
 *  through the medium yellow, to red at top-right), for a 0..1 diagonal score.
 *  Anchored to the priority flag palette so the two schemes stay one family. */
export function matrixColorHex(score: number): string {
  const s = clamp01(score) * 2;
  const seg = Math.min(1, Math.floor(s));
  const t = s - seg;
  const a = parseInt(MATRIX_STOP_HEX[seg]!.slice(1), 16);
  const b = parseInt(MATRIX_STOP_HEX[seg + 1]!.slice(1), 16);
  const mix = (sh: number) => Math.round(((a >> sh) & 0xff) + (((b >> sh) & 0xff) - ((a >> sh) & 0xff)) * t);
  const toHex = (c: number) => c.toString(16).padStart(2, "0");
  return `#${toHex(mix(16))}${toHex(mix(8))}${toHex(mix(0))}`;
}

/** Accent hex for an already-resolved matrix display position: the diagonal
 *  green→red gradient color at that spot. Null when there is no position.
 *  Callers pass the DERIVED display pos (`effectiveMatrixDisplayPos` /
 *  `matrixDisplayWithUrgency`) so deadline-driven urgency is reflected. */
export function matrixAccentFromPos(display: MatrixPos | null): string | null {
  return display ? matrixColorHex(matrixScoreOf(display)) : null;
}

/** Backdrop for a 3×3 matrix cell (col/row 0..2, bottom-left origin): the cell's
 *  diagonal gradient color plus a translucent wash of it, so any matrix grid
 *  (form picker, Priorities board) reads as one green→red surface. */
export function matrixCellWash(col: number, row: number): { color: string; wash: string } {
  const score = (matrixCellCenter(col) + matrixCellCenter(row)) / 2;
  const color = matrixColorHex(score);
  const alpha = Math.round(0x1a + score * (0x3d - 0x1a)).toString(16).padStart(2, "0");
  return { color, wash: `${color}${alpha}` };
}

/** How calendar/inbox blocks are softened toward white in the watercolor wash
 *  (see `block-wash.ts`). `task` / `event` are white-mix amounts ∈ [0,1]
 *  (0 = full saturation, 1 = white). Tasks render lighter, events more
 *  saturated — mirroring the card's wash while keeping the two distinct. */
export interface BlockTint {
  task: number;
  event: number;
}

/** Center of the wash softening range. The radial gradients + paper base
 *  already mute the color, so this sits lower than the card's `paperTint`. */
export const BLOCK_TINT_BASE = 0.4;
/** Default + max "strength of difference" between task and event tinting,
 *  exposed as a settings slider. */
export const BLOCK_TINT_DIFF_DEFAULT = 0.22;
export const BLOCK_TINT_DIFF_MAX = 0.45;

/** Derive per-kind tint amounts from a single "difference strength": tasks get
 *  lighter (base + half the diff), events more saturated (base − half). */
export function blockTintFromDiff(diff: number): BlockTint {
  const d = Math.max(0, Math.min(BLOCK_TINT_DIFF_MAX, diff)) / 2;
  return { task: Math.min(0.92, BLOCK_TINT_BASE + d), event: Math.max(0.05, BLOCK_TINT_BASE - d) };
}

export const DEFAULT_BLOCK_TINT: BlockTint = blockTintFromDiff(BLOCK_TINT_DIFF_DEFAULT);

/** Soften a hex color by blending it toward white. amount ∈ [0,1]; higher = paler. */
export function pastel(hex: string, amount = 0.5): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1]!, 16);
  const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
  const mix = (c: number) => Math.round(c + (255 - c) * amount);
  const toHex = (c: number) => c.toString(16).padStart(2, "0");
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}

/** Mix several topic/context colors into one representative color. Hex colors
 *  are averaged in JS so contrast checks still have a concrete fill; non-hex
 *  palette colors fall back to nested CSS `color-mix()` in oklch. */
export function mixColors(colors: readonly (string | null | undefined)[]): string | null {
  const clean = colors.filter((c): c is string => !!c && c.trim().length > 0);
  if (clean.length === 0) return null;
  if (clean.length === 1) return clean[0]!;

  const hexes = clean.map((c) => /^#?([0-9a-f]{6})$/i.exec(c.trim())?.[1] ?? null);
  if (hexes.every(Boolean)) {
    const totals = hexes.reduce(
      (acc, h) => {
        const n = parseInt(h!, 16);
        acc.r += (n >> 16) & 0xff;
        acc.g += (n >> 8) & 0xff;
        acc.b += n & 0xff;
        return acc;
      },
      { r: 0, g: 0, b: 0 },
    );
    const toHex = (n: number) => Math.round(n / hexes.length).toString(16).padStart(2, "0");
    return `#${toHex(totals.r)}${toHex(totals.g)}${toHex(totals.b)}`;
  }

  return clean.reduce((mix, color, i) =>
    i === 0 ? color : `color-mix(in oklch, ${mix} ${Math.round((i / (i + 1)) * 100)}%, ${color})`,
  );
}

/** A slightly-brighter shade of a parent task's color, so a subtask reads as a
 *  lighter relative of its parent. */
export function childTint(parentHex: string): string {
  return pastel(parentHex, 0.18);
}

/** A high-contrast ink for marks drawn ON a task's color (e.g. the completion
 *  checkmark): near-black on light/medium colors, near-white on dark ones —
 *  always strongly separated from the background. */
export function contrastShade(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return "#111827";
  const n = parseInt(m[1]!, 16);
  const lum = 0.299 * ((n >> 16) & 0xff) + 0.587 * ((n >> 8) & 0xff) + 0.114 * (n & 0xff);
  return lum > 140 ? "#111827" : "#f9fafb";
}

interface ColorTask {
  priority: Priority;
  custom_color: string | null;
  is_event?: boolean;
  subtask_parent_id?: string | null;
}

/** The hex a task actually renders with. Order: its own custom color → (if a
 *  subtask) a progressively-brighter shade INHERITED from its ancestor's color →
 *  its priority color → contextColor (topic/calendar) → event default. Pass
 *  `contextColor` from `useTaskContextColor` when available. */
export function effectiveColorOf<T extends ColorTask & { id?: string }>(
  task: T,
  tasks: ReadonlyArray<T>,
  depth = 0,
  contextColor?: string | null,
  /** Continuous matrix diagonal color for a matrix-PLACED task in scope
   *  (resolve via `useMatrixPriorityColor`); takes the discrete priority's slot
   *  so single-color surfaces (subtask accent tint, insert pill) track the
   *  exact position. Omitted/null ⇒ the discrete priority hue. */
  matrixColor?: string | null,
): string | null {
  if (task.custom_color) return task.custom_color;
  if (task.subtask_parent_id && depth < 20) {
    const parent = tasks.find((t) => t.id === task.subtask_parent_id);
    if (parent) {
      const c = effectiveColorOf(parent, tasks, depth + 1);
      if (c) return childTint(c);
    }
  }
  const pc = matrixColor || PRIORITY_HEX[task.priority];
  if (pc) return pc;
  if (contextColor) return contextColor;
  if (task.is_event) return EVENT_DEFAULT_HEX;
  return null;
}

/** When `task` is a subtask with no custom color of its own, synthesize a
 *  `custom_color` from its inherited (brighter) ancestor color so its chip/block
 *  visually inherits the parent's color — even if the subtask is an event or has
 *  a priority. Non-subtasks are returned unchanged (priority/event keep their
 *  usual tone-class rendering). */
export function inheritedColorTask<T extends ColorTask & { id?: string }>(
  task: T,
  tasks: ReadonlyArray<T>,
): T {
  if (task.custom_color || !task.subtask_parent_id) return task;
  const c = effectiveColorOf(task, tasks);
  return c ? { ...task, custom_color: c } : task;
}

/** Single effective hex for a task, used where one accent color is needed
 *  (e.g. a notification icon swatch). Priority: custom_color → contextColor →
 *  priority hex → (events only) slate default. Events are pastel-softened. */
export function notificationColor(
  task: { priority: Priority; custom_color: string | null; is_event?: boolean },
  contextColor?: string | null,
): string | null {
  const base = (task.custom_color ?? contextColor) ?? (PRIORITY_HEX[task.priority] || (task.is_event ? EVENT_DEFAULT_HEX : null));
  if (!base) return null;
  return task.is_event ? pastel(base) : base;
}

export type PriorityToneVariant = "chip" | "block" | "allday";

const TONE: Record<PriorityToneVariant, Record<Priority, string>> = {
  chip: {
    high: "bg-red-500/15 text-red-700 dark:text-red-300",
    medium: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    low: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
    none: "bg-secondary text-secondary-foreground",
  },
  block: {
    high: "bg-red-500/20 text-red-800 border-red-500/40 dark:text-red-200",
    medium: "bg-amber-500/20 text-amber-800 border-amber-500/40 dark:text-amber-200",
    low: "bg-blue-500/20 text-blue-800 border-blue-500/40 dark:text-blue-200",
    none: "bg-secondary text-secondary-foreground border-border",
  },
  allday: {
    high: "bg-red-500/20 text-red-800 dark:text-red-200",
    medium: "bg-amber-500/20 text-amber-800 dark:text-amber-200",
    low: "bg-blue-500/20 text-blue-800 dark:text-blue-200",
    none: "bg-primary/15 text-primary",
  },
};

/** Tailwind tone classes for a priority, per render context. Applied only when
 *  the task has no custom/context color (`washStyle` returns `washed: false`). */
export function priorityToneClass(priority: Priority, variant: PriorityToneVariant): string {
  return TONE[variant][priority];
}
